// A turn that FAILED must not reach chat as if the cybo had said it.
//
// THE INCIDENT (prod, 2026-08-11). A user's cybo answered a friendly "ok great
// fucking job" with, in full:
//
//   [System Error] Path "~/.cybo/agents/cybo_b99a556d-…" does not exist
//
// rendered THREE times for one message — twice as the cybo's own chat bubbles and
// once as the red error card between them. Nothing in it is actionable by the
// person reading it, and it is written in the cybo's voice, from the cybo's avatar.
//
// WHY IT IS FIXED HERE, IN THE UI, RATHER THAN AT THE PRODUCER. The fabrication is
// upstream: agent-manager.ts's appendSystemErrorTimelineMessage turns EVERY
// `turn_failed` into a real `assistant_message` timeline item prefixed
// "[System Error]" and dispatches it on the agent stream. That file is upstream
// Paseo code and packages/server/src/server/cyborg/audit-boundary.test.ts forbids
// this fork from touching anything under src/server/agent/ — so the producer is
// off limits and each downstream surface has to refuse the fabricated item itself:
//
//   bubble 1  → HERE. AgentStreamState drops the item instead of appending a
//               `kind:"text"` entry.
//   red card  → KEPT. The `turn_failed` event that follows draws TurnErrorRemedy,
//               which is the honest surface for a failed turn — so nothing is
//               silently swallowed by dropping the bubble; the failure still shows,
//               once, in the right shape, with the raw text under "Show details".
//               After a reload the durable row itself redraws that card (no event then).
//   bubble 2  → workspace-relay.ts's flushPendingAgentMessage, which stops the same
//               string becoming a durable PG row that would later re-merge (its
//               cross-session dedup keys on messageId, and the fabricated item
//               carries none, so it could never dedup against the live entry).
//
// Kept as its own module rather than inlined in state.svelte.ts so it is unit-
// testable without a Svelte render harness — same reason as interstitial-text.ts.
//
// ponytail: a prefix match, not a parser, and deliberately a SECOND spelling of the
// server's SYSTEM_ERROR_PREFIX rather than an import — packages/ui does not depend
// on packages/server. The prefix is produced by exactly one line upstream and is
// the whole contract; anything cleverer would have to be kept in sync with error
// text we do not own.

import type { StreamEntry } from "./state.svelte.js";

/**
 * The marker agent-manager.ts stamps on a fabricated turn-failure
 * "assistant message" (`[System Error] ${raw}`).
 */
export const SYSTEM_ERROR_PREFIX = "[System Error]";

/**
 * Is this "assistant text" actually a fabricated turn-failure notice?
 *
 * Anchored at the START (after leading whitespace) on purpose: a genuine reply
 * that merely quotes the marker mid-sentence — "the build failed with
 * [System Error] in the logs" — is a real answer and must still render.
 */
export function isSystemErrorText(text: string): boolean {
  return text.trimStart().startsWith(SYSTEM_ERROR_PREFIX);
}

/** The failure a persisted "[System Error]" row records: agent-manager's formatTurnFailedMessage. */
export interface PersistedTurnFailure {
  error: string;
  code?: string;
  diagnostic?: string;
  // The provider whose turn failed (rows written before it was stored have none).
  provider?: string;
}

export function parsePersistedTurnFailure(text: string): PersistedTurnFailure {
  return parseTurnFailureBody(text.trimStart().slice(SYSTEM_ERROR_PREFIX.length));
}

export function parseTurnFailureBody(body: string): PersistedTurnFailure {
  const [error = "", ...rest] = body.trim().split("\n\n");
  // A code is one token ("143", "session_ended"); a stderr line that starts "code: " is a diagnostic.
  const code = /^code: \S+$/.test(rest[0] ?? "") ? rest.shift()?.slice(6) : undefined;
  const provider = rest.at(-1)?.startsWith("provider: ") ? rest.pop()?.slice(10).trim() : undefined;
  const diagnostic = rest.join("\n\n").trim();
  return {
    error: error.trim(),
    ...(code ? { code } : {}),
    ...(diagnostic ? { diagnostic } : {}),
    ...(provider ? { provider } : {}),
  };
}

export function turnFailureCardIndex(
  entries: readonly StreamEntry[],
  error: string,
  among: { has(id: string): boolean },
): number {
  const wanted = error.trim();
  const prompts: StreamEntry[] = [];
  for (let i = entries.length - 1; i >= 0; i--) {
    const entry = entries[i];
    if (entry.kind === "user_message") prompts.push(entry);
    if (entry.kind !== "error" || !among.has(entry.id) || entry.content.trim() !== wanted) continue;
    // A prompt below a live card that it never saw (no seq yet, or one above its anchor) came after it.
    const anchor = entry.afterSeq;
    const after = (p: StreamEntry) => anchor !== undefined && (p.seq ?? Infinity) > anchor;
    return prompts.every(after) ? i : -1;
  }
  return -1;
}

export function isRuntimeExitRow(message: string): boolean {
  return message.trim().startsWith("Claude stopped unexpectedly (");
}

/** The failure a stored row records: a "[System Error]" reply or an idle-exit `error` row. */
export function storedFailureOf(item: {
  type: string;
  text?: unknown;
  message?: unknown;
}): PersistedTurnFailure | undefined {
  if (item.type === "assistant_message" && typeof item.text === "string") {
    return isSystemErrorText(item.text) ? parsePersistedTurnFailure(item.text) : undefined;
  }
  if (item.type !== "error" || typeof item.message !== "string") return undefined;
  return isRuntimeExitRow(item.message) ? parseTurnFailureBody(item.message) : undefined;
}
